import React, { useMemo, useState } from 'react';
import { User, ArrowDown, Info } from 'lucide-react';

/**
 * SanadTree — renders a vertical chain of narrators for one hadith.
 * Each chain is rendered as its own column. When multiple chains exist,
 * they are placed side-by-side with a subtle indicator of divergence
 * (same narrator highlighted in multiple chains).
 *
 * Props:
 *   chains: [{ chain_id, sanad_index, narrator_sequence: [narrator_id,...], ... }]
 *   narrators: { [narrator_id]: { name_en, name_ar, generation, gender, ... } }
 */
const GEN_COLORS = {
  Prophet: 'bg-emerald-900/40 border-emerald-500/50 text-emerald-100',
  'Comp_RA': 'bg-emerald-900/30 border-emerald-600/40 text-emerald-200',
  'Comp.(RA)': 'bg-emerald-900/30 border-emerald-600/40 text-emerald-200',
  'Follower_Tabi': 'bg-blue-900/30 border-blue-600/40 text-blue-200',
  "Follower(Tabi')": 'bg-blue-900/30 border-blue-600/40 text-blue-200',
  'Succ_TabaTabi': 'bg-indigo-900/30 border-indigo-600/40 text-indigo-200',
  'NotMapped': 'bg-gray-800/40 border-gray-600/40 text-gray-300',
};

const genLabel = {
  Prophet: 'Prophète ﷺ',
  'Comp_RA': 'Compagnon',
  'Comp.(RA)': 'Compagnon',
  'Follower_Tabi': 'Tābiʿ',
  "Follower(Tabi')": 'Tābiʿ',
  'Succ_TabaTabi': 'Tābiʿ al-Tābiʿ',
  'NotMapped': 'Non identifié',
};

const getTone = (gen) => GEN_COLORS[gen] || 'bg-surface-elevated border-border text-text-primary';

const NarratorNode = ({ narrator, narratorId, position, shared, onClick, isSelected }) => {
  const name = narrator?.name_en || `Narrateur ${narratorId}`;
  const nameAr = narrator?.name_ar || '';
  const gen = narrator?.generation || '';
  return (
    <button
      type="button"
      onClick={() => onClick?.(narratorId)}
      data-testid={`sanad-node-${narratorId}`}
      className={`w-full text-left rounded-lg border backdrop-blur-sm p-3 transition-all
        ${getTone(gen)}
        ${isSelected ? 'ring-2 ring-primary' : ''}
        ${shared ? 'shadow-[0_0_0_1px_rgba(217,165,102,0.5)]' : ''}
        hover:scale-[1.02] hover:shadow-lg`}
    >
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-black/30 flex items-center justify-center flex-shrink-0">
          <User className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight truncate">{name}</p>
          {nameAr && (
            <p className="font-arabic text-xs opacity-80 text-right mt-0.5" dir="rtl">
              {nameAr}
            </p>
          )}
          <p className="text-[10px] opacity-70 mt-1">
            {genLabel[gen] || gen || '—'} · #{position}
          </p>
        </div>
      </div>
      {shared && (
        <p className="text-[10px] text-primary mt-2">↔ Narrateur partagé</p>
      )}
    </button>
  );
};

export const SanadTree = ({ chains, narrators }) => {
  const [selectedNarrator, setSelectedNarrator] = useState(null);

  // Build map: narratorId -> set(chain_id) to detect shared narrators across chains
  const sharedSet = useMemo(() => {
    const byNarrator = {};
    chains.forEach(c => {
      (c.narrator_sequence || []).forEach(nid => {
        if (!byNarrator[nid]) byNarrator[nid] = new Set();
        byNarrator[nid].add(c.chain_id);
      });
    });
    const shared = new Set();
    Object.entries(byNarrator).forEach(([nid, set]) => {
      if (set.size > 1) shared.add(nid);
    });
    return shared;
  }, [chains]);

  if (!chains || chains.length === 0) {
    return (
      <div className="glass-card p-6 text-center text-text-muted">
        <Info className="w-5 h-5 mx-auto mb-2" />
        <p className="text-sm">Aucune donnée de sanad disponible pour ce hadith.</p>
        <p className="text-xs mt-1">
          Les chaînes de transmission sont extraites du jeu de données MIDS
          (Sahih Muslim uniquement pour l'instant).
        </p>
      </div>
    );
  }

  const maxLen = Math.max(...chains.map(c => (c.narrator_sequence || []).length));

  return (
    <div className="space-y-6" data-testid="sanad-tree">
      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        {[
          ['Comp_RA', 'Compagnon'],
          ['Follower_Tabi', 'Tābiʿ'],
          ['Succ_TabaTabi', 'Tābiʿ al-Tābiʿ'],
          ['NotMapped', 'Non identifié'],
        ].map(([k, label]) => (
          <span key={k} className={`px-2 py-0.5 rounded border ${getTone(k)}`}>
            {label}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <div
          className="grid gap-6 pb-2"
          style={{ gridTemplateColumns: `repeat(${chains.length}, minmax(240px, 1fr))` }}
        >
          {chains.map((chain, ci) => {
            const seq = chain.narrator_sequence || [];
            return (
              <div key={chain.chain_id} className="flex flex-col" data-testid={`sanad-chain-${chain.sanad_index}`}>
                <div className="mb-3 text-center">
                  <span className="overline">Isnad {chain.sanad_index}</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    {chain.narrator_count} narrateurs
                  </p>
                </div>
                <div className="space-y-0 flex-1">
                  {seq.map((nid, idx) => (
                    <React.Fragment key={`${chain.chain_id}-${idx}`}>
                      <NarratorNode
                        narrator={narrators[nid]}
                        narratorId={nid}
                        position={idx + 1}
                        shared={sharedSet.has(nid)}
                        isSelected={selectedNarrator === nid}
                        onClick={setSelectedNarrator}
                      />
                      {idx < seq.length - 1 && (
                        <div className="flex justify-center my-2">
                          <ArrowDown className="w-4 h-4 text-primary/50" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  {/* Pad to align chains of different lengths */}
                  {seq.length < maxLen && Array.from({ length: maxLen - seq.length }).map((_, i) => (
                    <div key={`pad-${i}`} className="h-[86px] opacity-0" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedNarrator && narrators[selectedNarrator] && (
        <div className="glass-card p-4" data-testid="sanad-selected-narrator">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-serif text-lg text-text-primary">
                {narrators[selectedNarrator].name_en}
              </h4>
              {narrators[selectedNarrator].name_ar && (
                <p className="font-arabic text-base text-primary mt-1 text-right" dir="rtl">
                  {narrators[selectedNarrator].name_ar}
                </p>
              )}
              <p className="text-xs text-text-muted mt-2">
                {genLabel[narrators[selectedNarrator].generation] ||
                  narrators[selectedNarrator].generation}
                {narrators[selectedNarrator].gender && ` · ${narrators[selectedNarrator].gender}`}
              </p>
            </div>
            <button
              className="text-xs text-text-muted hover:text-primary"
              onClick={() => setSelectedNarrator(null)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SanadTree;
